import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import React from 'react';

import { getVatCategories } from 'apis/vat-categories/api';
import { VatCategoryDefaultUseCase } from 'apis/vat-categories/types';

import { VatCategoriesResult, VatCategoryT } from '../types';

export type VatCategoriesParams = {
  country: Country;
  venueCity?: string;
  merchantAgreementParty?: string;
};

const VatCategoriesParamsContext = React.createContext<VatCategoriesParams | undefined>(undefined);

export const VatCategoriesParamsContextProvider = VatCategoriesParamsContext.Provider;

export const useVatCategories = (): VatCategoriesResult => {
  const params = React.useContext(VatCategoriesParamsContext);
  if (!params) {
    throw new Error('useVatCategories must be used within VatCategoriesParamsContextProvider');
  }

  const {
    data: vatCategories,
    isLoading,
    error,
  } = useQuery(['vat-categories', params.country], () => getVatCategories(params.country), {
    staleTime: 24 * 60 * 60 * 1000, // 24h
  });

  const result = useMemo(
    () => ({
      isLoading,
      error,
      data: vatCategories && {
        list: vatCategories,
        default:
          (isTaxFree(params) && chooseDefaultIfExists(vatCategories, 'MENU_EDITOR_TAXFREE')) ||
          chooseDefaultIfExists(vatCategories, 'MENU_EDITOR'),
      },
    }),
    [isLoading, error, vatCategories, params],
  );

  return result;
};

// Merchants signed contract with Eilat Wolt entity are cosidered tax-free and should have 0% selected by default
const isTaxFree = ({ country, venueCity, merchantAgreementParty }: VatCategoriesParams) =>
  country === 'ISR' && (venueCity === 'Eilat' || merchantAgreementParty === 'wolt_enterprises_eilat_ltd_isr');

const chooseDefaultIfExists = (vatCategories: VatCategoryT[] | undefined, useCase: VatCategoryDefaultUseCase) =>
  vatCategories?.find(vatCategory => vatCategory.default_for?.includes(useCase));
